import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { colors, fonts, radius, shadow, sectionLabel } from '../theme';

const CluesContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 18px;
  width: fit-content;
  height: fit-content;

  @media (max-width: 1024px) {
    width: 100%;
    max-width: 860px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
  }
`;

const ClueSection = styled.div`
  flex: 1;
  min-width: 268px;
  max-height: 540px;
  overflow-y: auto;
  background: ${colors.card};
  padding: 14px 6px 14px 14px;
  border: 1px solid ${colors.rule};
  border-radius: ${radius.md};
  box-shadow: ${shadow.panel};
`;

const ClueTitle = styled.h3`
  ${sectionLabel}
  color: ${colors.ink};
  margin: 0 0 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${colors.ruleStrong};
`;

const ClueList = styled.ul`
  margin: 0;
  padding-left: 0;
  list-style: none;
`;

/* The active clue is filled with the same yellow as the selected square, and
   the crossing clue gets the pale blue of the highlighted word — so the clue
   list and the grid use one shared colour language. */
const ClueItem = styled.li<{ $isActive?: boolean; $isSecondary?: boolean }>`
  display: flex;
  gap: 8px;
  line-height: 1.45;
  padding: 6px 10px 6px 8px;
  border-radius: ${radius.sm};
  cursor: pointer;
  font-size: 14px;
  color: ${colors.ink};
  background-color: ${props =>
    props.$isActive
      ? colors.cellSelected
      : props.$isSecondary
      ? colors.cellWord
      : 'transparent'};
  border-left: 3px solid
    ${props => (props.$isActive ? colors.cellSelectedEdge : 'transparent')};
  font-weight: ${props => (props.$isActive ? 600 : 400)};
  transition: background-color 0.1s ease;

  &:hover {
    background-color: ${props =>
      props.$isActive
        ? colors.cellSelected
        : props.$isSecondary
        ? colors.cellWord
        : colors.cardMuted};
  }

  strong {
    flex: 0 0 auto;
    min-width: 18px;
    font-family: ${fonts.body};
    font-weight: 700;
    color: ${props => (props.$isActive ? colors.ink : colors.inkFaint)};
  }
`;

interface CluesListProps {
  acrossClues: { [key: string]: string };
  downClues: { [key: string]: string };
  activeClueNumber?: string;
  activeDirection?: 'across' | 'down';
  secondaryClueNumber?: string;
  secondaryDirection?: 'across' | 'down';
  onClueClick?: (clueNumber: string, direction: 'across' | 'down') => void;
}

export const CluesList: React.FC<CluesListProps> = ({
  acrossClues,
  downClues,
  activeClueNumber,
  activeDirection,
  secondaryClueNumber,
  secondaryDirection,
  onClueClick,
}) => {
  const activeClueRef = useRef<HTMLLIElement>(null);

  const formatClues = (clues: { [key: string]: string }) => {
    return Object.entries(clues)
      .sort(([a], [b]) => parseInt(a) - parseInt(b))
      .map(([number, clue]) => ({ number, clue }));
  };

  useEffect(() => {
    if (activeClueRef.current) {
      activeClueRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [activeClueNumber, activeDirection]);

  return (
    <CluesContainer>
      <ClueSection className="scroll-panel">
        <ClueTitle>Across</ClueTitle>
        <ClueList>
          {formatClues(acrossClues).map(({ number, clue }) => {
            const isActive = activeClueNumber === number && activeDirection === 'across';
            const isSecondary = secondaryClueNumber === number && secondaryDirection === 'across';
            return (
              <ClueItem
                ref={isActive ? activeClueRef : null}
                key={`across-${number}`}
                $isActive={isActive}
                $isSecondary={isSecondary}
                onClick={() => onClueClick?.(number, 'across')}
              >
                <strong>{number}</strong>
                <span>{clue}</span>
              </ClueItem>
            );
          })}
        </ClueList>
      </ClueSection>

      <ClueSection className="scroll-panel">
        <ClueTitle>Down</ClueTitle>
        <ClueList>
          {formatClues(downClues).map(({ number, clue }) => {
            const isActive = activeClueNumber === number && activeDirection === 'down';
            const isSecondary = secondaryClueNumber === number && secondaryDirection === 'down';
            return (
              <ClueItem
                ref={isActive ? activeClueRef : null}
                key={`down-${number}`}
                $isActive={isActive}
                $isSecondary={isSecondary}
                onClick={() => onClueClick?.(number, 'down')}
              >
                <strong>{number}</strong>
                <span>{clue}</span>
              </ClueItem>
            );
          })}
        </ClueList>
      </ClueSection>
    </CluesContainer>
  );
};

export default CluesList;